import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Typography,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  IconButton,
  Divider,
  Paper,
  Avatar,
  //List,
  //ListItem,
  //ListItemText,
  Alert,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TimelineIcon from '@mui/icons-material/Timeline';
import PersonIcon from '@mui/icons-material/Person';
//import TableRowsIcon from '@mui/icons-material/TableRows';
// Import Recharts components
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import { convertDDSToWebp } from '../../utils/imageUtils';
import { useSelector } from 'react-redux';
import { getItemHistory, getItemQuantityProgression, getItemCounts, type ItemCountResponse } from '../../services/itemService';
//import type { AppDispatch } from '../../store/store';
import { selectGameFilter, selectLeagueFilter } from '../../store/slices/filterSlice';
import type { Item } from '../../store/slices/itemsSlice';

// Define the TabPanel component for handling tab content
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`item-history-tabpanel-${index}`}
      aria-labelledby={`item-history-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface ItemHistoryEntry {
  id: string;
  quantity: number;
  timestamp: string;
  characterName?: string;
  item_slot?: string;
  inventory_type?: string;
}

// This type is for the recharts data format
interface ChartDataPoint {
  date: string;
  displayDate: string;
  quantity: number;
}

interface ItemDetailsProps {
  open: boolean;
  onClose: () => void;
  item: Item | null;
  characterId?: string; // Optional character context
}

const ItemDetailsPanel: React.FC<ItemDetailsProps> = ({ open, onClose, item, characterId }) => {
  //const dispatch = useDispatch<AppDispatch>();
  //const theme = useTheme();
  //const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // State for item history and tabs
  const [itemHistory, setItemHistory] = useState<ItemHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();

  // Chart data state
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);

  // Character breakdown state
  const [characterBreakdown, setCharacterBreakdown] = useState<ItemCountResponse[]>([]);
  const [breakdownLoading, setBreakdownLoading] = useState<boolean>(false);
  const [breakdownError, setBreakdownError] = useState<string | null>(null);

  // Get selected game and league from filter state
  const selectedGame = useSelector(selectGameFilter);
  const selectedLeague = useSelector(selectLeagueFilter);

  // Fetch item history when an item is selected
  useEffect(() => {
    if (item && open) {
      fetchItemHistory();
      fetchCharacterBreakdown();
    }
    // Clear history when panel closes
    if (!open) {
      setItemHistory([]);
      setHistoryError(null);
      setCharacterBreakdown([]);
      setBreakdownError(null);
    }
  }, [item, open, selectedGame, selectedLeague, characterId]);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const fetchItemHistory = async () => {
    if (!item) return;

    setHistoryLoading(true);
    setHistoryError(null);

    try {
      let history: ItemHistoryEntry[];

      if (characterId) {
        // Fetch detailed history for a specific character
        const response = await getItemHistory(item.id, characterId);
        history = response.map((entry: any) => ({
          id: entry.id || entry.timestamp,
          quantity: entry.quantity || 0,
          timestamp: entry.timestamp || entry.createdAt,
          characterName: entry.character_name || undefined,
          item_slot: entry.item_slot || undefined,
          inventory_type: entry.inventory_type || undefined,
        }));
      } else {
        // Fetch aggregated quantity progression for a summed item
        const response = await getItemQuantityProgression(item.id, selectedLeague || '');
        history = response.map(entry => ({
          id: entry.date,
          quantity: entry.total_quantity,
          timestamp: entry.date,
        }));
      }

      // Sort by timestamp (newest first for the list view)
      history.sort((a: ItemHistoryEntry, b: ItemHistoryEntry) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      setItemHistory(history);

      // Prepare chart data (sorted chronologically - oldest first)
      const chartDataPoints = [...history]
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        .map(entry => {
          const date = new Date(entry.timestamp);
          return {
            date: date.toISOString(),
            displayDate:
              date.toLocaleDateString() +
              ' ' +
              date.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              }),
            quantity: entry.quantity,
          };
        });

      setChartData(chartDataPoints);
    } catch (error: any) {
      console.error('Failed to fetch item history:', error);
      setHistoryError(error.response?.data?.message || 'Failed to load item history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchCharacterBreakdown = async () => {
    if (!item || !selectedGame || !selectedLeague) return;

    setBreakdownLoading(true);
    setBreakdownError(null);

    try {
      const response = await getItemCounts({
        itemName: item.name,
        game: selectedGame,
        league: selectedLeague,
      });

      // Ensure response is an array
      if (Array.isArray(response)) {
        // Filter out characters with 0 count and sort by count descending
        const filtered = response.filter(char => char.count > 0).sort((a, b) => b.count - a.count);

        setCharacterBreakdown(filtered);
      } else {
        console.error('Expected array response but got:', response);
        setCharacterBreakdown([]);
      }
    } catch (error: any) {
      console.error('Failed to fetch character breakdown:', error);
      setBreakdownError(error.response?.data?.message || 'Failed to load character breakdown');
    } finally {
      setBreakdownLoading(false);
    }
  };

  if (!item) {
    return null;
  }

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '600px', md: '700px' },
          maxWidth: '100%',
          padding: 2,
          overflow: 'auto',
        },
      }}
    >
      {/* Header with item name and close button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant='h5' component='h2'>
          Item Details
        </Typography>
        <IconButton onClick={onClose} aria-label='close'>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Item basic information */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Item icon */}
          <Box>
            <Avatar
              src={convertDDSToWebp(item.properties?.ddsPath, item.game)}
              alt={item.name}
              variant='square'
              sx={{ width: 64, height: 64 }}
            >
              {item.name.substring(0, 1)}
            </Avatar>
          </Box>
          {/* Item details */}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant='h6'>{item.name}</Typography>
            <Typography variant='body1' color='text.secondary'>
              {item.item_type || 'Unknown Type'}
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Chip label={`Quantity: ${item.quantity}`} size='small' color='primary' />
              {item.item_slot && <Chip label={`Slot: ${item.item_slot}`} size='small' variant='outlined' />}
              {item.inventory_type && <Chip label={item.inventory_type} size='small' variant='outlined' />}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Character Breakdown section */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PersonIcon sx={{ mr: 1 }} />
          <Typography variant='h6'>Character Breakdown</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {breakdownLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : breakdownError ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {breakdownError}
          </Alert>
        ) : characterBreakdown.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            No characters currently own this item.
          </Typography>
        ) : (
          <List dense sx={{ py: 0 }}>
            {characterBreakdown.map(char => (
              <ListItem key={char.character_id} sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Avatar sx={{ width: 32, height: 32 }}>{char.character_name.substring(0, 1).toUpperCase()}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={char.character_name}
                  secondary={
                    <Box component='span'>
                      <Typography component='span' variant='body2'>
                        {`${char.count} ${char.count === 1 ? 'item' : 'items'}`}
                      </Typography>
                      {char.character_hostname && (
                        <Typography component='span' variant='body2' color='text.secondary' sx={{ ml: 1 }}>
                          • {char.character_hostname}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Chip label={char.count} size='small' color='primary' sx={{ ml: 1 }} />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Item history section with tabs */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TimelineIcon sx={{ mr: 1 }} />
          <Typography variant='h6'>Quantity History</Typography>
        </Box>
        <Divider sx={{ mb: 1 }} />

        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : historyError ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {historyError}
          </Alert>
        ) : itemHistory.length === 0 ? (
          <Typography variant='body1' sx={{ py: 2 }}>
            No history available for this item.
          </Typography>
        ) : (
          <>
            <Tabs value={tabValue} onChange={handleTabChange} variant='fullWidth' aria-label='Item history view options'>
              <Tab icon={<TimelineIcon />} label='Quantity' id='item-history-tab-0' aria-controls='item-history-tabpanel-0' />
            </Tabs>

            {/* Quantity Graph view */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width='100%' height='100%'>
                  <LineChart
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 20,
                      left: 10,
                      bottom: 30,
                    }}
                  >
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis
                      dataKey='displayDate'
                      angle={-45}
                      textAnchor='end'
                      height={70}
                      tick={{ fontSize: 10 }}
                      tickFormatter={value => {
                        // Simplify date display for x-axis
                        return value.split(' ')[0]; // Show only the date part
                      }}
                    />
                    <YAxis
                      domain={['dataMin - 1', 'dataMax + 1']}
                      label={{
                        value: 'Quantity',
                        angle: -90,
                        position: 'insideLeft',
                        style: { textAnchor: 'middle' },
                      }}
                    />
                    <Tooltip labelFormatter={value => `Time: ${value}`} formatter={(value: number) => [`Quantity: ${value}`, 'Quantity']} />
                    <Legend verticalAlign='top' height={36} />
                    <ReferenceLine y={0} stroke='#000' />
                    <Line
                      type='monotone'
                      dataKey='quantity'
                      stroke={theme.palette.primary.main}
                      activeDot={{ r: 8 }}
                      name='Quantity'
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </TabPanel>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default ItemDetailsPanel;
